import { queryOptions } from '@tanstack/react-query';
import { isAssetId } from '@/shared/lib/asset-id';
import type { ListBrandMediaQuery } from '@/shared/lib/brand-media';
import { listBrandMedia } from './brandMedia.api';

/* eslint-disable i18next/no-literal-string -- cache key segments, not copy */
export const BRAND_MEDIA_QUERY_KEYS = {
  /** Every brand-media query. What an upload invalidates, since it changes what any of them answer. */
  all: ['brand-media'] as const,
  /** The folder tree. Under the same prefix, so an upload or a move invalidates it too. */
  folders: ['brand-media', 'folders'] as const,
  bound: (assetIds: readonly string[]) => ['brand-media', 'bound', [...assetIds].sort()] as const,
  /** One listing. Keyed on the whole question, so changing a filter or a search term refetches. */
  list: (query: ListBrandMediaQuery) => ['brand-media', 'list', query] as const,
};
/* eslint-enable i18next/no-literal-string */

/**
 * One listing, asked exactly as given.
 *
 * No `staleTime`: the library is a surface people upload to and file within while looking at it, so a
 * window where the grid still shows the answer from before their own move would read as the move
 * having failed.
 */
export const brandMediaQueryOptions = (query: ListBrandMediaQuery) =>
  queryOptions({
    queryKey: BRAND_MEDIA_QUERY_KEYS.list(query),
    queryFn: () => listBrandMedia(query),
  });

/**
 * The library records for assets a board has already bound.
 *
 * Asked for by id rather than filtered, because a bound asset is bound whatever the filters would
 * have said: a photograph whose market was narrowed after it was chosen still has to render, and
 * the frame that holds it still has to be able to show what it is.
 */
export const boundMediaQueryOptions = (assetIds: readonly string[]) =>
  queryOptions({
    queryKey: BRAND_MEDIA_QUERY_KEYS.bound(assetIds),
    enabled: assetIds.length > 0,
    staleTime: 5 * 60 * 1000,
    // The library validates every id and refuses the whole request on one malformed value, and a
    // refused request here is silent: the rows carry each picture's crop, so every crop on the
    // board would quietly fall back to a plain cover. Ask only for what it will accept.
    queryFn: () => {
      const accepted = assetIds.filter(isAssetId);
      return accepted.length > 0 ? listBrandMedia({ assetIds: accepted }) : Promise.resolve([]);
    },
  });
